import { BrowserRouter, Navigate, Route, Routes } from "react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@web/lib/queryClient.ts";
import { Toaster } from "@web/components/ui/sonner.tsx";
import { AdminGate } from "@web/features/auth/AdminGate.tsx";
import { AuthGate } from "@web/features/auth/AuthGate.tsx";
import { RequireAuth } from "@web/features/auth/RequireAuth.tsx";
import { SignInPage } from "@web/features/auth/SignInPage.tsx";
import { DebugHud } from "@web/features/diagnostics/DebugHud.tsx";
import { ErrorBoundary } from "@web/features/diagnostics/ErrorBoundary.tsx";
import { Shell } from "@web/features/shell/Shell.tsx";
import { ArtistsPage } from "@web/features/library/ArtistsPage.tsx";
import { AlbumsPage } from "@web/features/library/AlbumsPage.tsx";
import { GenresPage } from "@web/features/library/GenresPage.tsx";
import { LibraryPage } from "@web/features/library/LibraryPage.tsx";
import { PlaylistExplorePage } from "@web/features/library/PlaylistExplorePage.tsx";
import { PlaylistsPage } from "@web/features/library/PlaylistsPage.tsx";
import { SettingsPage } from "@web/features/settings/SettingsPage.tsx";
import { WorkbenchPage } from "@web/features/workbench/WorkbenchPage.tsx";
import { IdeasPage } from "@web/features/ideas/IdeasPage.tsx";
import { ComposePage } from "@web/features/ideas/ComposePage.tsx";
import { GlobePage } from "@web/features/globe/GlobePage.tsx";

export function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthGate>
          <BrowserRouter>
            <Routes>
              <Route path="/sign-in/*" element={<SignInPage />} />
              <Route element={<RequireAuth />}>
                <Route element={<Shell />}>
                  <Route index element={<Navigate to="/liked" replace />} />
                  <Route path="liked" element={<LibraryPage kind="liked" />} />
                  <Route path="all" element={<LibraryPage kind="all" />} />
                  <Route path="artists" element={<ArtistsPage />} />
                  <Route path="albums" element={<AlbumsPage />} />
                  <Route path="playlists" element={<PlaylistsPage />} />
                  <Route path="genres" element={<GenresPage />} />
                  <Route path="globe" element={<GlobePage />} />
                  <Route path="explore/globe" element={<Navigate to="/globe" replace />} />
                  <Route path="playlist/:id" element={<LibraryPage kind="playlist" />} />
                  <Route
                    path="playlist/:id/explore"
                    element={<PlaylistExplorePage kind="playlist" />}
                  />
                  <Route path="local/:id" element={<LibraryPage kind="local" />} />
                  <Route
                    path="local/:id/explore"
                    element={<PlaylistExplorePage kind="local" />}
                  />
                  <Route path="view/:id" element={<LibraryPage kind="view" />} />
                  <Route path="ideas" element={<IdeasPage />} />
                  <Route path="ideas/:id" element={<ComposePage />} />
                  <Route path="candidates" element={<Navigate to="/ideas" replace />} />
                  <Route path="story" element={<Navigate to="/ideas" replace />} />
                  <Route path="settings" element={<SettingsPage />} />
                  <Route
                    path="workbench"
                    element={
                      <AdminGate>
                        <WorkbenchPage />
                      </AdminGate>
                    }
                  />
                  <Route path="*" element={<Navigate to="/liked" replace />} />
                </Route>
              </Route>
            </Routes>
          </BrowserRouter>
        </AuthGate>
        <Toaster position="bottom-center" />
        <DebugHud />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
